import { useEffect, useRef, useState } from 'react'

import { ButtonTypes } from '@cox/core-ui8/dist/Button'
import { FormMessage } from '@cox/core-ui8/dist/FormMessage'

import { Line, ReactCalculatorProps, Recommendation } from './types'
import {
  //  CHEVRON_DOWN_BLUE,
  PLUS
} from '../../assets/images'
import { SAVINGS_CALCULATOR_PATH } from '../../constants'
import { getSavingsCalculatorRecommendations } from '../../services'
// import { Dropdown } from '../../utils'
import { DynamicMediaComponents, getDynamicMediaFromSrc } from '../../utils/dynamic-media-util'
import { getImageSrc } from '../../utils/local-util'
import { DynamicBannerStatus } from '../../utils/types'
import ReactButton from '../ReactButton'
import ReactImage from '../ReactImage'
import ReactSectionHeader from '../ReactSectionHeader'
import ReactText from '../ReactText'
import IsInEditorText from '../shared/IsInEditorText'
import './ReactCalculator.scss'

const ReactCalculator = (props: ReactCalculatorProps): JSX.Element => {
  const {
    isInEditor = false,
    eyebrow = '',
    title = '',
    description = '',
    examplebill = '',
    // providerOptions = [],
    receiptImage = '',
    imageParams = '',
    lineOptions = [],
    // dropdownIcon = CHEVRON_DOWN_BLUE,
    betterOffer = '<p>Looks like you’re getting a good deal, but there’s still plenty you’re missing out on.<a>Learn more</a></p>',
    noSelections = '<p>Enter your bill information to see how much you can save.</p>',
    addIcon = PLUS,
    jsonPath = SAVINGS_CALCULATOR_PATH,
    monthlyBillLabel = 'Monthly bill',
    removeLine = 'Remove line',
    clearSelections = 'Clear selections'
  } = props

  let {
    receiptDescription = '<p>One’s a streaming fiend. The other just needs the essentials. With Cox Mobile, they could save up to <span class="cox-text-savings-calculator">$XXX</span>/year. That\'s <span class="cox-text-savings-calculator">$YYY</span>/mo.</p>',
    savingsNote = '<p>With Cox Mobile, they could save up to <span class="cox-text-savings-calculator">$XXX</span>/year. That\'s <span class="cox-text-savings-calculator">$YYY</span>/mo.</p>'
  } = props

  const [recommendations, setRecommendations] = useState([])
  // const [selectedProvider, setSelectedProvider] = useState(providerOptions[0])
  const [monthlyBill, setMonthlyBill] = useState(139)
  const [lines, setLines] = useState<Line[]>([])
  const [receiptDetails, setReceiptDetails] = useState({
    monthlySavings: 0,
    yearlySavings: 0,
    estimatedMonthlyTotal: 0,
    showReceipt: false,
    showBetterOffer: false,
    showWelcomeNote: true
  })
  const { showWelcomeNote, monthlySavings, yearlySavings, estimatedMonthlyTotal, showBetterOffer, showReceipt } =
    receiptDetails
  savingsNote = savingsNote.replace('XXX', yearlySavings.toString()).replace('YYY', monthlySavings.toString())
  receiptDescription = receiptDescription.replace('XXX', yearlySavings.toString()).replace('YYY', monthlySavings.toString())
  const [receiptRecommendation, setReceiptRecommendation] = useState<Recommendation>()
  const [showErrorOnMonthlyBill, setShowErrorOnMonthlyBill] = useState(false)
  const [showErrorOnSelections, setShowErrorOnSelections] = useState(false)
  const addLineText = lines.length > 0 ? 'Add another line' : 'Add a line'
  const showClearSelections = lines.some((line) => line.planID)
  const hasSelectionsOnAllLines = lines.every((line) => line.planID)
  const savingsRef = useRef<HTMLDivElement>(null)
  const selectionErrorMessageRef = useRef<HTMLDivElement>(null)
  const inputErrorMessageRef = useRef<HTMLDivElement>(null)

  // const handleSelectProvider = (provider: string) => {
  //   setSelectedProvider(provider)
  // }
  const handleChangeBill = (e: any) => {
    const input = e.target.value
    const filteredBill = input.replace(/\D/g, '')
    setMonthlyBill(filteredBill)
    setShowErrorOnMonthlyBill(!filteredBill ? true : false)
    if (!filteredBill) {
      focusSelectionErrorMessage()
    }
  }

  const focusSelectionErrorMessage = () => {
    setTimeout(() => {
      if (selectionErrorMessageRef) {
        const { current } = selectionErrorMessageRef
        if (current) {
          current.focus()
        }
      }
      if (inputErrorMessageRef) {
        const { current } = inputErrorMessageRef
        if (current) {
          current.focus()
        }
      }
    }, 350)
  }

  const handleCalculate = (lines: Line[], showWelcomeDesc: boolean, scroll?: boolean) => {
    let plan1Count = 0
    let plan2Count = 0
    let plan3Count = 0
    lines.forEach((line) => {
      switch (line.planID) {
        case '1gb':
          plan1Count += 1
          break
        case '2gb':
          plan2Count += 1
          break
        case '3gb':
          plan3Count += 1
          break
      }
    })
    const recommendation: any = recommendations.find((rec: Recommendation) => {
      return rec.id == [plan1Count, plan2Count, plan3Count].join('')
    })
    setReceiptRecommendation(recommendation)
    const { monthlyRate: calculatedBill = 0 } = recommendation || {}
    const monthlySaving = monthlyBill - calculatedBill

    if (!monthlyBill || monthlySaving <= 0 || calculatedBill == 0 || !hasSelectionsOnAllLines) {
      setReceiptDetails((prevState) => ({
        ...prevState,
        showReceipt: false,
        showBetterOffer: (monthlySaving <= 0 || calculatedBill == 0) && hasSelectionsOnAllLines
      }))
      setShowErrorOnMonthlyBill(!monthlyBill ? true : false)
      if (!monthlyBill) {
        focusSelectionErrorMessage()
      }
      setShowErrorOnSelections(hasSelectionsOnAllLines ? false : true)
      if (!hasSelectionsOnAllLines) {
        focusSelectionErrorMessage()
      }
    } else {
      const yearlySaving = monthlySaving * 12
      setReceiptDetails((prevState) => ({
        ...prevState,
        monthlySavings: monthlySaving,
        yearlySavings: yearlySaving,
        showReceipt: true,
        estimatedMonthlyTotal: calculatedBill,
        showWelcomeNote: showWelcomeDesc
      }))
    }
    if (savingsRef.current && scroll) {
      savingsRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleAddLine = () => {
    const newLines = [...lines]
    const lineDetails = {
      planName: '',
      planDescription: '',
      rate: '',
      planID: ''
    }
    newLines.push(lineDetails)
    setLines(newLines)
    setReceiptDetails((prevState) => ({ ...prevState, showReceipt: false }))
  }
  const handleRemoveLine = (index: number) => {
    const newLines = [...lines]
    newLines.splice(index, 1)
    setLines(newLines)
    setReceiptDetails((prevState) => ({ ...prevState, showReceipt: false }))
  }

  const handleSelectOption = (index: number, option: Line) => {
    const newLines = [...lines]
    const lineDetail = {
      planName: option.planName,
      planDescription: option.planDescription,
      rate: option.rate,
      planID: option.planID
    }
    newLines[index] = lineDetail
    setLines(newLines)
    setShowErrorOnSelections(newLines.every((line) => line.planID) ? false : true)
  }
  const handleClearSelections = () => {
    let newLines = [...lines]
    newLines = newLines.map(() => {
      return { planName: '', planDescription: '', rate: '', planID: '' }
    })
    setLines(newLines)
    setReceiptDetails((prevState) => ({ ...prevState, showReceipt: false, showBetterOffer: false }))
  }
  const generateReceiptLineDetails = () => {
    const recommendationLine = {
      line1: receiptRecommendation?.lines.dataTier1.count,
      line1Rate: receiptRecommendation?.lines.dataTier1.rate,
      line2: receiptRecommendation?.lines.dataTier2.count,
      line2Rate: receiptRecommendation?.lines.dataTier2.rate,
      line3: receiptRecommendation?.lines.dataTier3.count,
      line3Rate: receiptRecommendation?.lines.dataTier3.rate
    }
    const { line1 = 0, line2 = 0, line3 = 0, line1Rate = 0, line2Rate = 0, line3Rate = 0 } = recommendationLine
    const getLinesDetails = (lineCount: number, lineRate: number, lineType: number) => {
      return (
        <div className='line'>
          <div className='cox-text-title3-bold'>{`${lineCount} Line${lineCount > 1 ? 's' : ''}`}</div>
          {lineType !== 3 && <div className='cox-text-paragraph2-regular'>{`Pay As You Gig`}</div>}
          <div className='line-rate cox-text-paragraph3-regular'>
            <span>{lineType !== 3 ? `(${lineType}GB @ $15/GB per month)` : 'Gig Unlimited'}</span>
            <span>{`$${lineRate / lineCount}/mo`}</span>
          </div>
        </div>
      )
    }
    if (receiptRecommendation) {
      return (
        <>
          {line1 > 0 && getLinesDetails(line1, line1Rate, 1)}
          {line2 > 0 && getLinesDetails(line2, line2Rate, 2)}
          {line3 > 0 && getLinesDetails(line3, line3Rate, 3)}
        </>
      )
    }
  }

  useEffect(() => {
    if (recommendations.length == 0) {
      getSavingsCalculatorRecommendations({ setRecommendations, jsonPath })
    }

    //Add lines and make selections on initial load
    const addLines = (option: Line) => {
      return {
        planName: option.planName,
        planDescription: option.planDescription,
        rate: option.rate,
        planID: option.planID
      }
    }
    if (lineOptions.length > 0) {
      const linesAdded = []
      linesAdded.push(addLines(lineOptions[0]))
      linesAdded.push(addLines(lineOptions[2]))
      setLines(linesAdded)

      //Click on calculate for the bill information
      if (recommendations.length > 0) {
        handleCalculate(linesAdded, true)
      }
    }
  }, [recommendations])

  const getCalculatorHeader = () => {
    return (
      <ReactSectionHeader
        eyebrow={{ value: eyebrow, isRTE: true }}
        title={{ value: title, isRTE: true }}
        description={{
          containerClass: 'description',
          className: 'description',
          value: description,
          isRTE: true
        }}
        isInEditor={isInEditor}
      />
    )
  }

  const getCalculatorBill = () => {
    return (
      <div className='calculator-bill'>
        <div className='calculator-line-details'>
          <div className='bill-title cox-text-heading3'>{examplebill}</div>
          <div className='bill-inputs'>
            {/* <div className='bill-provider-details'>
              <div className='bill-provider-title cox-text-title3-bold'>{'Current mobile provider'}</div>
              {providerOptions?.length > 0 && (
                <Dropdown>
                  <Dropdown.Toggle id='dropdown-basic' placeholder='Choose an option'>
                    {selectedProvider}
                    <div className='icon'>
                      <img src={getImageSrc(dropdownIcon)} alt='chevron-down-icon' />
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {providerOptions.map((provider: string) => {
                      return (
                        <Dropdown.Item onClick={() => handleSelectProvider(provider)} key={provider}>
                          {provider}
                        </Dropdown.Item>
                      )
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div> */}
            <div className='monthly-bill-details'>
              <div className='monthly-bill-title cox-text-title3-bold'>{monthlyBillLabel}</div>
              <div className='monthly-bill-input'>
                {'$'}
                <input
                  className='bill-input-field cox-text-title1-medium'
                  type='tel'
                  value={monthlyBill}
                  onChange={(e) => handleChangeBill(e)}
                  aria-label='Username'
                  aria-describedby='basic-addon1'
                  placeholder='Enter Amount'
                />
                {'/mo'}
              </div>
              {showErrorOnMonthlyBill && (
                <div tabIndex={0} ref={inputErrorMessageRef}>
                  <FormMessage
                    status={DynamicBannerStatus.ERROR}
                    message={'Please, input your monthly bill.'}
                    requiredMessageIconAlt='Validation error'
                  />
                </div>
              )}
            </div>
            <div></div>
          </div>
          {lines?.length > 0 &&
            lines?.map((line: Line, idx: any) => {
              return (
                <div className='line' key={line.planID + idx}>
                  <div className='line-header'>
                    <span>Line {idx + 1}</span>
                    <a onClick={() => handleRemoveLine(idx)} key={idx}>
                      {removeLine}
                    </a>
                  </div>
                  {lineOptions?.length > 0 && (
                    <div className='line-options'>
                      {lineOptions.map((option: Line, index: any) => {
                        return (
                          <span
                            className={`line-option ${line.planName === option.planName ? 'selected' : ''}`}
                            onClick={() => handleSelectOption(idx, option)}
                            key={option.planName + idx + index}
                          >
                            {option.planName}
                          </span>
                        )
                      })}
                    </div>
                  )}
                  <span className='line-description'>
                    <ReactText text={line.planDescription} />
                  </span>
                </div>
              )
            })}
          {lines.length < 5 && (
            <a onClick={() => handleAddLine()} className='add-line-text cox-text-interactive2'>
              {addLineText}
              <img className='add-icon' src={getImageSrc(addIcon)} alt='add-icon'></img>
            </a>
          )}
        </div>
        {lines.length > 0 && (
          <div>
            {showErrorOnSelections && (
              <div ref={selectionErrorMessageRef} tabIndex={0}>
                <FormMessage
                  status={DynamicBannerStatus.ERROR}
                  message={'Please, make selections on all the lines.'}
                  requiredMessageIconAlt='Validation error'
                />
              </div>
            )}
            <div className='calculate-buttons'>
              <ReactButton
                text={'Calculate'}
                customClickEvent={() => handleCalculate(lines, false, true)}
                buttonTypes={ButtonTypes.PRIMARY}
              />
              {showClearSelections && (
                <ReactButton
                  text={clearSelections}
                  customClickEvent={() => handleClearSelections()}
                  buttonTypes={ButtonTypes.TERTIARY}
                />
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  const getCalculatorReceipt = () => {
    return (
      <div className='calculator-receipt'>
        <div className='receipt-icon'>
          <ReactImage
            src={getDynamicMediaFromSrc(
              receiptImage,
              (imageParams as DynamicMediaComponents) || DynamicMediaComponents.TRANSPARENT_BG
            )}
            alt={'image for receipt'}
          />
        </div>

        <div className='receipt-details' ref={savingsRef}>
          {!showReceipt ? (
            <div className='receipt-details-wrapper'>
              <div className='receipt-description'>
                {showBetterOffer ? <ReactText text={betterOffer} /> : <ReactText text={noSelections} />}
              </div>
            </div>
          ) : (
            <div className='receipt-details-wrapper'>
              <div className='receipt-description'>
                {showWelcomeNote ? <ReactText text={receiptDescription} /> : <ReactText text={savingsNote} />}
              </div>
              <div className='receipt-line-details'>
                <hr className='seperator' />
                {generateReceiptLineDetails()}
                <hr className='seperator' />
                <div className='final-bill'>
                  <div className='cox-text-title3-bold'>{'Estimated monthly total'}</div>
                  <div className='final-price cox-text-paragraph3-regular'>
                    <span>{'Before taxes and fees'}</span>
                    <span>{`$${estimatedMonthlyTotal}/mo`}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div data-testid={`react-calculator-container`} className={`react-calculator-container container`} id={props.id}>
      {IsInEditorText(isInEditor, 'React Calculator')}
      {lineOptions.length > 0 && recommendations.length > 0 && (
        <>
          {getCalculatorHeader()}
          <div className='calculator-body-container'>
            {getCalculatorBill()}
            {getCalculatorReceipt()}
          </div>
        </>
      )}
    </div>
  )
}

export default ReactCalculator
